
import React, { useState } from "react";
import PDFToImage from "./PDFToImage";
import PDFToText from "./PDFToText";
import PDFMergeSplit from "./PDFMergeSplit";
import PDFToWordExcel from "./PDFToWordExcel";
import PDFToPPT from "./PDFToPPT";
import CompletePDFEditor from "./CompletePDFEditor";

const features = [
  { label: "🎨 PDF Editor (Create & Edit)", value: "editor" },
  { label: "🖼️ PDF to Images (JPG/PNG)", value: "toImage" },
  { label: "📝 PDF to Text", value: "toText" },
  { label: "🔗 Merge PDFs", value: "merge" },
  { label: "✂️ Split PDF", value: "split" },
  { label: "📄 PDF to Word/Excel", value: "toWordExcel" },
  { label: "🎬 PDF to PowerPoint", value: "toPPT" },
];

export default function PDFConverter() {
  const [feature, setFeature] = useState("");
  const [file, setFile] = useState(null);

  return (
    <div style={{ 
      maxWidth: '100%',
      width: '100%',
      margin: '0 auto', 
      background: 'rgba(255, 255, 255, 0.95)', 
      borderRadius: 'clamp(12px, 4vw, 24px)', 
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)', 
      padding: 'clamp(16px, 4vw, 40px)',
      backdropFilter: 'blur(10px)',
      border: '2px solid rgba(255, 255, 255, 0.5)',
      boxSizing: 'border-box',
    }}>
      <div style={{ marginBottom: 'clamp(16px, 4vw, 32px)', textAlign: 'center' }}>
        <label htmlFor="feature-select" style={{ 
          fontWeight: 700, 
          fontSize: 'clamp(16px, 4vw, 20px)', 
          display: 'block', 
          marginBottom: 'clamp(12px, 3vw, 16px)',
          color: '#333',
          letterSpacing: '0.5px'
        }}>
          Choose your magic ✨
        </label>
        <select
          id="feature-select"
          value={feature}
          onChange={e => {
            setFeature(e.target.value);
            setFile(null);
            // Reset file input visually
            setTimeout(() => {
              const input = document.getElementById('pdf-upload');
              if (input) input.value = null;
            }, 0);
          }}
          style={{ 
            padding: 'clamp(10px, 2.5vw, 14px)', 
            borderRadius: 12, 
            fontSize: 'clamp(14px, 3vw, 16px)', 
            width: '100%',
            border: '2px solid #e0e0e0',
            background: '#fff',
            cursor: 'pointer',
            transition: 'all 0.2s',
            fontWeight: 500
          }}
        >
          <option value="" disabled>Select type... 🎯</option>
          {features.map(f => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
      </div>

      {/* Show file input only for non-merge and non-editor features */}
      {feature && feature !== "merge" && feature !== "editor" && (
        <div style={{ marginBottom: 32, textAlign: 'center' }}>
          <label htmlFor="pdf-upload" style={{ 
            fontWeight: 600, 
            fontSize: 18, 
            display: 'block', 
            marginBottom: 16,
            color: '#555'
          }}>
            Upload your PDF 📎
          </label>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <input
              id="pdf-upload"
              type="file"
              accept="application/pdf"
              onChange={e => setFile(e.target.files[0] || null)}
              style={{
                padding: 14,
                border: '2px dashed #ff6b9d',
                borderRadius: 12,
                background: '#fff',
                width: '100%',
                fontSize: 16,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            />
            {file && (
              <button
                onClick={() => { setFile(null); document.getElementById('pdf-upload').value = null; }}
                style={{ 
                  color: '#fff', 
                  background: '#ff6b9d', 
                  border: 'none', 
                  borderRadius: '50%',
                  width: 32,
                  height: 32,
                  fontWeight: 'bold', 
                  fontSize: 20, 
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Remove file"
                onMouseOver={(e) => e.currentTarget.style.background = '#ff4081'}
                onMouseOut={(e) => e.currentTarget.style.background = '#ff6b9d'}
              >×</button>
            )}
          </div>
          {file && <div style={{ marginTop: 12, color: '#555', fontSize: 15, fontWeight: 500 }}>✅ {file.name}</div>}
        </div>
      )}

      {/* Complete PDF Editor: Create & Edit in one */}
      {feature === "editor" && (
        <div style={{ 
          background: "#fff", 
          padding: 0,
          borderRadius: 16, 
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          border: '2px solid #f0f0f0',
          animation: 'slideIn 0.3s ease-out',
          overflow: 'hidden',
        }}>
          <CompletePDFEditor />
        </div>
      )}

      {/* Merge PDFs: show only merge inputs */}
      {feature === "merge" && (
        <div style={{ 
          background: "#fff", 
          padding: 28, 
          borderRadius: 16, 
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          border: '2px solid #f0f0f0'
        }}>
          <PDFMergeSplit mode="merge" />
        </div>
      )}

      {/* Other features: show result only if file is selected */}
      {feature && feature !== "merge" && feature !== "editor" && file && (
        <div style={{ 
          background: "#fff", 
          padding: 28, 
          borderRadius: 16, 
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          border: '2px solid #f0f0f0',
          animation: 'slideIn 0.3s ease-out'
        }}>
          {feature === "toImage" && <PDFToImage file={file} />}
          {feature === "toText" && <PDFToText file={file} />}
          {feature === "split" && <PDFMergeSplit file={file} mode="split" />}
          {feature === "toWordExcel" && <PDFToWordExcel file={file} />}
          {feature === "toPPT" && <PDFToPPT file={file} />}
        </div>
      )}
    </div>
  );
}
